import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { ThumbsUp, HandHeart, Sparkles, Heart, MessageCircle, ImagePlus, Send, Trash2, X, Loader2, Award } from "lucide-react";
import { toast } from "sonner";
import { MILESTONES } from "@/components/LevelBadge";

const REACTIONS = [
  { key: "like", label: "Like", icon: ThumbsUp },
  { key: "pray", label: "Pray", icon: HandHeart },
  { key: "amen", label: "Amen", icon: Sparkles },
  { key: "love", label: "Love", icon: Heart },
];

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

function Avatar({ src, name, size = 44 }) {
  return src ? (
    <img src={src} alt={name} className="object-cover shrink-0" style={{ width: size, height: size }} />
  ) : (
    <div className="bg-navy text-bone flex items-center justify-center font-serif shrink-0" style={{ width: size, height: size, fontSize: size / 2.4 }}>{name?.[0]}</div>
  );
}

export function FeedList({ wallOwnerId = null, wallOwnerName = null }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [body, setBody] = useState("");
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/feed", { params: wallOwnerId ? { wall_owner_id: wallOwnerId } : {} })
      .then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoaded(true));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wallOwnerId]);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/feed/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(data.image);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!body.trim() && !image) { toast.error("Write something or add a photo"); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/feed", { body: body.trim(), image, wall_owner_id: wallOwnerId });
      setPosts((p) => [data, ...p]); setBody(""); setImage("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const onDelete = (id) => setPosts((p) => p.filter((x) => x.id !== id));

  const placeholder = wallOwnerId
    ? (wallOwnerId === user?.id ? "Share an update with the brotherhood…" : `Write on ${wallOwnerName || "their"} wall…`)
    : "Share an update, a praise, or a word of encouragement…";

  return (
    <div data-testid="feed-list">
      {/* Composer */}
      <div className="border border-charcoal/10 bg-bone p-5" data-testid="post-composer">
        <div className="flex gap-4">
          <Avatar src={user?.avatar} name={user?.name} />
          <div className="flex-1">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder={placeholder} data-testid="post-body"
              className="w-full bg-transparent font-sans text-charcoal/90 placeholder:text-charcoal/40 focus:outline-none resize-none" />
            {image && (
              <div className="relative mt-3 inline-block">
                <img src={image} alt="attachment" className="max-h-52 border border-charcoal/10" />
                <button onClick={() => setImage("")} data-testid="remove-image" className="absolute top-2 right-2 bg-charcoal/80 text-bone p-1 hover:bg-rust transition-colors"><X size={14} /></button>
              </div>
            )}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-charcoal/10">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="add-photo-btn"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-charcoal/60 hover:text-navy transition-colors disabled:opacity-50">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} Photo
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pickImage} data-testid="post-image-input" />
              <button onClick={submit} disabled={posting} data-testid="post-submit"
                className="inline-flex items-center gap-2 bg-charcoal text-bone px-6 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
                <Send size={13} /> {posting ? "Posting..." : "Post"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Posts */}
      <div className="mt-6 space-y-5">
        {posts.map((p) => <PostCard key={p.id} post={p} me={user} onDelete={onDelete} showWall={!wallOwnerId} />)}
        {loaded && posts.length === 0 && (
          <p className="font-sans text-charcoal/50 text-center py-12">No posts yet. Be the first to share.</p>
        )}
      </div>
    </div>
  );
}

function PostCard({ post, me, onDelete, showWall }) {
  const [react, setReact] = useState({ reactions: post.reactions, my_reaction: post.my_reaction, total_reactions: post.total_reactions });
  const [showReacts, setShowReacts] = useState(false);
  const [openComments, setOpenComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [commentBody, setCommentBody] = useState("");
  const [count, setCount] = useState(post.comment_count);

  const doReact = async (type) => {
    setShowReacts(false);
    try {
      const { data } = await api.post(`/feed/${post.id}/react`, { type });
      setReact(data);
    } catch { toast.error("Try again"); }
  };

  const toggleComments = async () => {
    const next = !openComments; setOpenComments(next);
    if (next && comments === null) {
      try { const { data } = await api.get(`/feed/${post.id}/comments`); setComments(data); }
      catch { setComments([]); }
    }
  };

  const addComment = async () => {
    if (!commentBody.trim()) return;
    try {
      const { data } = await api.post(`/feed/${post.id}/comments`, { body: commentBody.trim() });
      setComments((c) => [...(c || []), data]); setCommentBody(""); setCount((n) => n + 1);
    } catch { toast.error("Could not comment"); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/feed/${post.id}`); onDelete(post.id); toast.success("Post deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const myMeta = REACTIONS.find((r) => r.key === react.my_reaction);
  const MyIcon = myMeta?.icon || ThumbsUp;
  const activeReacts = REACTIONS.filter((r) => react.reactions[r.key] > 0);
  const ms = post.milestone ? MILESTONES[post.milestone] : null;

  return (
    <div className={`border bg-bone ${ms ? "border-gold shadow-[0_0_0_1px_rgba(228,163,22,0.3)]" : "border-charcoal/10"}`} data-testid={`post-${post.id}`}>
      {ms && (
        <div className="flex items-center gap-2 bg-gold/15 border-b border-gold/30 px-5 py-2.5" data-testid={`milestone-ribbon-${post.id}`}>
          {ms.icon && <ms.icon size={15} className="text-gold" strokeWidth={1.8} />}
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-navy">Milestone · {ms.name}</span>
          <Sparkles size={13} className="text-gold ml-auto" />
        </div>
      )}
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Link to={`/members/${post.author_id}`}><Avatar src={post.author_avatar} name={post.author} /></Link>
            <div>
              <div className="flex items-center gap-2">
                <Link to={`/members/${post.author_id}`} className="font-serif text-lg tracking-tight hover:underline">{post.author}</Link>
                {post.author_role === "admin" && <span className="font-mono text-[9px] uppercase tracking-widest bg-gold text-navy px-1.5 py-0.5">Ministry</span>}
                {showWall && post.wall_owner_id && post.wall_owner_id !== post.author_id && (
                  <span className="font-sans text-xs text-charcoal/50">▸ <Link to={`/members/${post.wall_owner_id}`} className="hover:underline">{post.wall_owner_name}</Link></span>
                )}
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">{timeAgo(post.created_at)}</span>
            </div>
          </div>
          {(post.is_author || me?.role === "admin") && (
            <button onClick={remove} data-testid={`delete-post-${post.id}`} className="text-charcoal/30 hover:text-rust transition-colors"><Trash2 size={16} /></button>
          )}
        </div>

        {post.body && <p className="font-sans text-charcoal/90 mt-4 whitespace-pre-line leading-relaxed">{post.body}</p>}
      </div>
      {post.image && <img src={post.image} alt="" className="w-full max-h-[540px] object-cover" data-testid={`post-image-${post.id}`} />}

      <div className="px-5 md:px-6 py-3 flex items-center justify-between border-t border-charcoal/5">
        <div className="flex items-center gap-1.5">
          {activeReacts.map((r) => <r.icon key={r.key} size={13} className="text-navy" />)}
          {react.total_reactions > 0 && <span className="font-mono text-[10px] text-charcoal/50 ml-1">{react.total_reactions}</span>}
        </div>
        {count > 0 && <button onClick={toggleComments} className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50 hover:text-navy">{count} comment{count === 1 ? "" : "s"}</button>}
      </div>

      <div className="px-3 py-1 flex items-center border-t border-charcoal/10 relative">
        <div className="relative">
          <button onClick={() => react.my_reaction ? doReact(react.my_reaction) : setShowReacts((s) => !s)}
            onMouseEnter={() => setShowReacts(true)}
            data-testid={`react-btn-${post.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors ${react.my_reaction ? "text-navy" : "text-charcoal/60 hover:text-navy"}`}>
            <MyIcon size={16} className={react.my_reaction ? "fill-navy/10" : ""} /> {myMeta?.label || "React"}
          </button>
          {showReacts && !react.my_reaction && (
            <div onMouseLeave={() => setShowReacts(false)} data-testid={`react-menu-${post.id}`}
              className="absolute bottom-full left-2 mb-1 flex items-center gap-1 bg-bone border border-charcoal/15 shadow-lg p-1.5 z-20">
              {REACTIONS.map((r) => (
                <button key={r.key} onClick={() => doReact(r.key)} title={r.label} data-testid={`react-${r.key}-${post.id}`}
                  className="p-2 hover:bg-sand transition-colors" >
                  <r.icon size={18} className="text-navy" />
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={toggleComments} data-testid={`comment-btn-${post.id}`}
          className="flex items-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-charcoal/60 hover:text-navy transition-colors">
          <MessageCircle size={16} /> Comment
        </button>
      </div>

      {openComments && (
        <div className="px-5 md:px-6 py-4 border-t border-charcoal/10 bg-sand/30" data-testid={`comments-${post.id}`}>
          <div className="space-y-4">
            {(comments || []).map((c) => (
              <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                <Avatar src={c.author_avatar} name={c.author} size={32} />
                <div className="bg-bone border border-charcoal/10 px-4 py-2.5 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-serif text-sm">{c.author}</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-charcoal/40">{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="font-sans text-sm text-charcoal/80 mt-0.5 whitespace-pre-line">{c.body}</p>
                </div>
              </div>
            ))}
            {comments && comments.length === 0 && <p className="font-sans text-sm text-charcoal/40">No comments yet.</p>}
          </div>
          <div className="flex gap-3 mt-4">
            <Avatar src={me?.avatar} name={me?.name} size={32} />
            <div className="flex-1 flex gap-2">
              <input value={commentBody} onChange={(e) => setCommentBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()}
                placeholder="Write a comment…" data-testid={`comment-input-${post.id}`}
                className="flex-1 border-b border-charcoal/30 bg-transparent py-2 font-sans text-sm focus:outline-none focus:border-navy" />
              <button onClick={addComment} data-testid={`comment-submit-${post.id}`} className="text-navy hover:text-charcoal transition-colors"><Send size={16} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
